import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import { listSellerPresales, sellerDecide, sellerWithdraw, parseInquiryError, REASON_CODE_LABELS } from "@/lib/modulD";
import { ArrowLeft, Check, X, Edit3, AlertCircle, ShoppingBag, Clock } from "lucide-react";

const STATUS_LABEL = {
  DRAFT: "Entwurf",
  SUBMITTED: "Neu",
  SELLER_ACCEPTED: "Akzeptiert",
  SELLER_MODIFIED: "Gegenvorschlag offen",
  SELLER_REJECTED: "Abgelehnt",
  BUYER_COUNTER_ACCEPTED: "Buyer angenommen",
  BUYER_COUNTER_DECLINED: "Buyer abgelehnt",
  TEMP_LISTING_CREATED: "Privates Angebot erstellt",
  CLOSED: "Abgeschlossen",
  CLOSED_ITEM_UNAVAILABLE: "Artikel nicht mehr verfügbar",
};
const STATUS_COLOR = {
  SUBMITTED: "text-amber-600 bg-amber-50 border-amber-200",
  SELLER_ACCEPTED: "text-green-600 bg-green-50 border-green-200",
  SELLER_MODIFIED: "text-blue-600 bg-blue-50 border-blue-200",
  SELLER_REJECTED: "text-red-600 bg-red-50 border-red-200",
  TEMP_LISTING_CREATED: "text-green-700 bg-green-100 border-green-300",
  BUYER_COUNTER_ACCEPTED: "text-green-700 bg-green-100 border-green-300",
  BUYER_COUNTER_DECLINED: "text-red-600 bg-red-50 border-red-200",
  CLOSED: "text-[#999] bg-[#FAFAFA] border-[#E5E5E5]",
  CLOSED_ITEM_UNAVAILABLE: "text-[#999] bg-[#FAFAFA] border-[#E5E5E5]",
};

export default function SellerRequestsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null); // presale_id
  const [modPrice, setModPrice] = useState("");
  const [modMessage, setModMessage] = useState("");
  const [working, setWorking] = useState(false);
  const [errorBanner, setErrorBanner] = useState(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSellerPresales();
      setRequests(data.requests || []);
    } catch { setRequests([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    loadRequests();
  }, [user, authLoading, loadRequests, navigate]);

  const decide = async (presaleId, decision) => {
    setWorking(true);
    setErrorBanner(null);
    try {
      const body = { decision };
      if (decision === "MODIFY") {
        const price = parseFloat(modPrice);
        if (!price || price <= 0) {
          setErrorBanner({ msg: "Bitte gültigen Preis eingeben." });
          setWorking(false);
          return;
        }
        body.counter_price = price;
        body.message = modMessage || null;
      }
      if (decision === "REJECT" && modMessage) body.message = modMessage;
      await sellerDecide(presaleId, body);
      setDeciding(null);
      setModPrice("");
      setModMessage("");
      await loadRequests();
    } catch (e) {
      const parsed = parseInquiryError(e);
      if (parsed.blocked) {
        setErrorBanner({ msg: `${REASON_CODE_LABELS[parsed.reason_code] || "Blockiert"} – ${parsed.reason_text}` });
      } else {
        setErrorBanner({ msg: parsed.message || e?.response?.data?.detail || "Fehler" });
      }
    } finally {
      setWorking(false);
    }
  };

  const withdraw = async (presaleId) => {
    if (!confirm(t("seller_requests.confirm_withdraw"))) return;
    setWorking(true);
    try {
      await sellerWithdraw(presaleId);
      await loadRequests();
    } catch (e) {
      setErrorBanner({ msg: e?.response?.data?.detail || "Fehler beim Zurückziehen" });
    } finally { setWorking(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="seller-requests-page" className="pb-20">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="requests-back" onClick={() => navigate(-1)} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A]">{t("seller_requests.title")}</h1>
        <span className="ml-auto text-xs text-[#999]">{requests.length} Anfragen</span>
      </div>

      {errorBanner && (
        <InfoCard variant="danger" className="mx-4 mt-3">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /><span>{errorBanner.msg}</span>
        </InfoCard>
      )}

      {requests.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <p className="text-sm text-[#999]">{t("seller_requests.empty")}</p>
        </div>
      ) : (
        <div className="divide-y divide-[#E5E5E5]">
          {requests.map((r) => (
            <PresaleSellerCard key={r.presale_id} request={r}
              deciding={deciding === r.presale_id}
              setDeciding={(b) => { setDeciding(b ? r.presale_id : null); setModPrice(""); setModMessage(""); setErrorBanner(null); }}
              modPrice={modPrice} setModPrice={setModPrice}
              modMessage={modMessage} setModMessage={setModMessage}
              onDecide={(d) => decide(r.presale_id, d)}
              onWithdraw={() => withdraw(r.presale_id)}
              working={working} navigate={navigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function PresaleSellerCard({ request: r, deciding, setDeciding, modPrice, setModPrice, modMessage, setModMessage, onDecide, onWithdraw, working, navigate }) {
  const { t } = useTranslation();
  const canDecide = r.status === "SUBMITTED";
  const isCounterPending = r.status === "SELLER_MODIFIED" && r.buyer_counter_response_status === "PENDING";
  const isClosedSuccess = r.status === "TEMP_LISTING_CREATED";
  const counterCount = r.counter_round_count || 0;

  return (
    <div data-testid={`request-${r.presale_id}`} className="px-4 py-4">
      <div className="flex items-start gap-3">
        {r.listing?.media_urls?.[0] ? (
          <div className="w-12 h-14 bg-[#F5F5F5] shrink-0 overflow-hidden">
            <img src={r.listing.media_urls[0]} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-12 h-14 bg-[#F5F5F5] shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold text-[#0A0A0A]">{r.buyer_alias || "Buyer"}</p>
            <span className={`text-[10px] px-2 py-0.5 border ${STATUS_COLOR[r.status] || "text-[#999] border-[#E5E5E5]"}`}>
              {STATUS_LABEL[r.status] || r.status}
            </span>
          </div>
          {r.listing?.title && (
            <p className="text-[10px] text-[#999] truncate mt-0.5">Bezug: {r.listing.title}</p>
          )}
          <p className="text-sm text-[#0A0A0A] leading-relaxed mt-1.5 whitespace-pre-wrap">{r.item_description}</p>
          {r.preferences && <p className="text-xs text-[#999] mt-1">Wunsch: {r.preferences}</p>}
          {r.budget && <p className="text-xs text-[#666] mt-1">Budget: {Number(r.budget).toFixed(2)} EUR</p>}
          {r.moderation?.redaction_count > 0 && (
            <p className="text-[10px] text-amber-600 mt-1 italic">⚠ {r.moderation.redaction_count} Kontaktdaten-Marker entfernt</p>
          )}
          <p className="text-[10px] text-[#CCC] mt-1">{r.created_at ? new Date(r.created_at).toLocaleString("de-DE") : ""}</p>
        </div>
      </div>

      {/* Counter Offer Status (after MODIFY) */}
      {r.counter_offer && (
        <InfoCard variant="info" className="mt-3">
          <p className="font-bold text-blue-700 mb-0.5">Dein Gegenangebot: {Number(r.counter_offer.price).toFixed(2)} EUR</p>
          {r.counter_offer.message && <p className="text-blue-700">"{r.counter_offer.message}"</p>}
          <p className="text-[10px] text-blue-600 mt-1">
            {r.buyer_counter_response_status === "PENDING" && "⏳ Wartet auf Buyer-Antwort"}
            {r.buyer_counter_response_status === "ACCEPTED" && "✓ Buyer hat angenommen"}
            {r.buyer_counter_response_status === "DECLINED" && "✗ Buyer hat abgelehnt"}
          </p>
        </InfoCard>
      )}

      {/* Temp Listing visible */}
      {isClosedSuccess && r.temp_listing_id && (
        <InfoCard variant="success" className="mt-3">
          <p className="font-bold text-green-700 flex items-center gap-1"><ShoppingBag size={12} /> {t("seller_requests.private_created")}</p>
          <p className="text-green-700 mt-0.5">{t("seller_requests.buyer_can_buy")}</p>
          <button onClick={onWithdraw} disabled={working}
            className="mt-1.5 text-[10px] text-red-600 underline disabled:opacity-40">{t("seller_requests.withdraw")}</button>
        </InfoCard>
      )}

      {/* Decide actions (only on SUBMITTED) */}
      {canDecide && !deciding && (
        <div className="mt-3 flex gap-2 flex-wrap">
          <button data-testid={`accept-${r.presale_id}`} onClick={() => onDecide("ACCEPT")} disabled={working}
            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium hover:bg-green-500 transition-colors disabled:opacity-40">
            <Check size={12} /> Annehmen
          </button>
          <button data-testid={`modify-${r.presale_id}`} onClick={() => setDeciding(true)} disabled={working || counterCount >= 1}
            className="flex items-center gap-1 px-3 py-1.5 bg-[#0A0A0A] text-white text-xs font-medium hover:bg-[#222] transition-colors disabled:opacity-40">
            <Edit3 size={12} /> Gegenvorschlag
          </button>
          <button data-testid={`reject-${r.presale_id}`} onClick={() => onDecide("REJECT")} disabled={working}
            className="flex items-center gap-1 px-3 py-1.5 border border-red-300 text-red-500 text-xs font-medium hover:bg-red-50 transition-colors disabled:opacity-40">
            <X size={12} /> Ablehnen
          </button>
        </div>
      )}

      {/* Modify Form */}
      {deciding && (
        <div className="mt-3 space-y-2 p-3 border border-[#E5E5E5] bg-[#FAFAFA]">
          <p className="text-[10px] text-[#666] font-bold uppercase tracking-wider">Gegenangebot</p>
          <input data-testid="mod-price" type="number" step="0.01" min="0.01" value={modPrice} onChange={(e) => setModPrice(e.target.value)}
            className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={`Neuer Preis (EUR) ${r.budget ? `– Budget Buyer: ${r.budget}` : ""}`} />
          <input data-testid="mod-message" value={modMessage} onChange={(e) => setModMessage(e.target.value)}
            className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("seller_requests.msg_placeholder")} />
          <p className="text-[10px] text-[#999]">{t("seller_requests.counter_note")}</p>
          <div className="flex gap-2">
            <button onClick={() => setDeciding(false)} disabled={working} className="rounded-full h-8 px-3 border border-[#E5E5E5] text-xs text-[#666] disabled:opacity-40">{t("seller_requests.cancel")}</button>
            <button data-testid="mod-submit" onClick={() => onDecide("MODIFY")} disabled={working || !modPrice}
              className="flex-1 h-8 bg-[#0A0A0A] text-white text-xs font-medium disabled:opacity-40">{working ? "..." : "Gegenvorschlag senden"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
